import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Alert, Button, Col, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isEmail } from 'react-multi-email'
import useTitle from 'hooks/useTitle'
import LoadingSpinner from 'Components/LoadingSpinner'
import LoadingSpinnerAlt from 'Components/LoadingSpinnerAlt'
import ErrorMessage from 'Components/ErrorMessage'
import DatePicker from 'Components/DatePicker'
import WithholdingEmailSendModal from 'Components/Modals/WithholdingEmailSendModal'
import Service from 'Service'
import uiStore from 'stores/uiStore'
import emailSendModalStore from 'stores/Components/emailSendModalStore'
import withholdingsStore from 'stores/withholdingsStore'
import Utilities from 'Utilities'
import useStores from 'hooks/useStores'
import { withholdingsQueryKey } from 'hooks/queryKeys'
import { INVOICES_EDIT, WITHHOLDINGS_DETAIL, WITHHOLDINGS_LIST } from 'Constants/Routes'
import { parseApiCivilDate, toApiCivilDate } from 'Utilities/dateTime'
import { bindRef } from 'Utilities/formRef'
import * as yup from 'yup'
import './Detail.scss'

const DetailActionButton = ({
  variant,
  icon,
  label,
  onClick,
  type = 'button',
  disabled,
  busy,
}) => {
  const button = (
    <Button
      type={type}
      variant={variant}
      className="wh-detail-action-btn"
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={label}
    >
      {busy ? <LoadingSpinnerAlt /> : <i className={icon} aria-hidden="true" />}
      <span className="wh-detail-action-btn__label">{label}</span>
    </Button>
  )

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={`wh-action-${label}`}>{label}</Tooltip>}
    >
      <span className="d-inline-flex">{button}</span>
    </OverlayTrigger>
  )
}

const isTxtLinked = (withholding) => Boolean(withholding?.GenerateTxt)

const WithholdingDetail = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { companyStore } = useStores()
  const countryId = companyStore.selectedCompany?.IdCountry || 1
  const companyKey = companyStore.selectedCompany?.Keycompany

  useTitle('withholding_detail')

  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [releasing, setReleasing] = useState(false)

  const validationSchema = yup.object().shape({
    Date: yup.date().required(),
    ImpositivePeriod: yup.string().required(),
  })

  const { register, handleSubmit, setValue, errors, control, reset } = useForm({
    validationSchema,
  })

  const { data: withholding, isLoading, isError, refetch } = useQuery({
    queryKey: ['withholding', companyKey, id],
    queryFn: () => Service.Withholding.getById(id),
    enabled: Boolean(companyKey && id),
  })

  useEffect(() => {
    if (!withholding) return
    reset({
      Date: parseApiCivilDate(withholding.Date) || new Date(),
      Correlative: withholding.Correlative,
      ImpositivePeriod: withholding.ImpositivePeriod,
    })
  }, [withholding, reset])

  const canEdit = withholding && !isTxtLinked(withholding)
  const canDelete = withholding && !isTxtLinked(withholding)

  async function updateImpositivePeriod(ymdDate) {
    const date = parseApiCivilDate(ymdDate) || new Date()
    const dateStr = toApiCivilDate(date)
    try {
      const result = await Service.FiscalCalendar.getImpositivePeriod(countryId, dateStr)
      setValue('ImpositivePeriod', result?.ImpositivePeriod || buildLocalPeriod(date))
    } catch {
      setValue('ImpositivePeriod', buildLocalPeriod(date))
    }
  }

  function buildLocalPeriod(date) {
    const month = date.getMonth() + 1
    const monthStr = month < 10 ? `0${month}` : `${month}`
    return `${date.getFullYear()}${monthStr}`
  }

  function handleDateChange([value]) {
    updateImpositivePeriod(value)
    return value
  }

  async function onSubmit(data) {
    if (!canEdit) return
    setSaving(true)
    try {
      await Service.Withholding.update(id, { Date: toApiCivilDate(data.Date) })
      uiStore.notifySuccess(t('withholding_updated_successfully'))
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['withholdings', companyKey] })
    } catch (err) {
      const message = err?.response?.data?.message || t('error_saving_data')
      uiStore.notifyError(message)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!canDelete) return
    const confirmed = await uiStore.confirm({
      title: t('confirm_delete_title'),
      message: t('confirm_delete_withholding'),
      confirmLabel: t('delete'),
      cancelLabel: t('cancel'),
      variant: 'danger',
    })
    if (!confirmed) return

    setDeleting(true)
    try {
      await Service.Withholding.remove(id)
      uiStore.notifySuccess(t('withholding_deleted_successfully'))
      queryClient.invalidateQueries({ queryKey: withholdingsQueryKey(companyKey) })
      queryClient.invalidateQueries({ queryKey: ['withholdings', companyKey] })
      history.push(WITHHOLDINGS_LIST)
    } catch (err) {
      const message = err?.response?.data?.message || t('error_saving_data')
      uiStore.notifyError(message)
    } finally {
      setDeleting(false)
    }
  }

  function handleSendEmail() {
    const email = withholding?.Provider?.Email
    emailSendModalStore.openModal(id, isEmail(email) ? [email] : [])
  }

  async function handleDownload() {
    setDownloading(true)
    try {
      await withholdingsStore.downloadDocument(id, withholding.Correlative)
    } catch {
      uiStore.notifyError(t('error_saving_data'))
    } finally {
      setDownloading(false)
    }
  }

  async function handleReleaseInvoice(invoice) {
    if (!canEdit) return
    const confirmed = await uiStore.confirm({
      title: t('attention'),
      message: t('confirm_release_invoice_from_withholding', {
        invoice: invoice.InvoiceNumber || '',
      }),
      confirmLabel: t('release_from_withholding'),
      cancelLabel: t('cancel'),
      variant: 'warning',
    })
    if (!confirmed) return

    setReleasing(true)
    try {
      await Service.Withholding.releaseInvoice(id, invoice.Id)
      uiStore.notifySuccess(t('invoice_released_from_withholding'))
      await refetch()
      queryClient.invalidateQueries({ queryKey: ['withholdings', companyKey] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    } catch (err) {
      const message = err?.response?.data?.message || t('error_saving_data')
      uiStore.notifyError(message)
    } finally {
      setReleasing(false)
    }
  }

  const actionsBusy = saving || deleting || downloading || releasing

  if (isLoading) return <LoadingSpinner />
  if (isError || !withholding) {
    return (
      <div className="mt-4">
        <Alert variant="warning">{t('withholding_not_found')}</Alert>
        <Button variant="secondary" onClick={() => history.push(WITHHOLDINGS_LIST)}>
          {t('back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-4 pb-4 withholding-detail">
      <div className="mb-3">
        <h2 className="mb-1">{t('withholding_detail')}</h2>
        <p className="text-muted mb-0">
          {t('correlative')}: {withholding.Correlative}
        </p>
      </div>

      {isTxtLinked(withholding) && (
        <Alert variant="info">
          {t('withholding_locked_by_txt')}
        </Alert>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={!canEdit || actionsBusy}>
          <Row>
            <Col xs={12} md={3}>
              <Form.Group controlId="whDetailDate">
                <Form.Label>{t('date')}</Form.Label>
                <Controller
                  as={DatePicker}
                  control={control}
                  name="Date"
                  onChange={handleDateChange}
                  disabled={!canEdit}
                />
                <ErrorMessage error={errors?.Date} />
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group controlId="whDetailCorrelative">
                <Form.Label>{t('correlative')}</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  name="Correlative"
                  ref={bindRef(register)}
                  defaultValue={withholding.Correlative}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group controlId="whDetailPeriod">
                <Form.Label>{t('impositive_period')}</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  name="ImpositivePeriod"
                  ref={bindRef(register)}
                  defaultValue={withholding.ImpositivePeriod}
                />
                <ErrorMessage error={errors?.ImpositivePeriod} />
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>{t('txt_generated_date')}</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  value={
                    isTxtLinked(withholding)
                      ? Utilities.TransformDateTime(withholding.DateGenerate)
                      : t('txt_not_generated_yet')
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mt-3">{t('provider')}</h5>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>RIF</Form.Label>
                <Form.Control readOnly value={withholding.Provider?.Rif || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('provider')}</Form.Label>
                <Form.Control readOnly value={withholding.Provider?.BusinessName || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('email')}</Form.Label>
                <Form.Control readOnly value={withholding.Provider?.Email || ''} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('phone') || 'Teléfono'}</Form.Label>
                <Form.Control readOnly value={withholding.Provider?.PhoneNumber || ''} />
              </Form.Group>
            </Col>
          </Row>
        </fieldset>

        <h5 className="mt-4">{t('invoices')}</h5>
        {(!withholding.Invoices || withholding.Invoices.length === 0) && (
          <p className="text-muted">{t('not_invoices_found')}</p>
        )}
        {withholding.Invoices?.length > 0 && (
          <Table striped bordered hover responsive size="sm" className="wh-detail-invoices-table">
            <thead>
              <tr className="text-center">
                <th>{t('invoice_number') || 'Nro. factura'}</th>
                <th>{t('control_number') || 'Nro. control'}</th>
                <th>{t('date')}</th>
                <th>{t('total')}</th>
                <th>IVA</th>
                <th>ISLR</th>
                {canEdit && <th>{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {withholding.Invoices.map((invoice) => (
                <tr key={invoice.Id} className="text-center">
                  <td>{invoice.InvoiceNumber}</td>
                  <td>{invoice.ControlNumber}</td>
                  <td>{Utilities.TransformDate(invoice.EmitDate)}</td>
                  <td>{Utilities.FormatCurrency(invoice.Total || 0)}</td>
                  <td>{Utilities.FormatCurrency(invoice.WithholdingAmmount || 0)}</td>
                  <td>{Utilities.FormatCurrency(invoice.IslrWithholdingAmount || 0)}</td>
                  {canEdit && (
                    <td className="wh-detail-invoice-actions">
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`edit-inv-${invoice.Id}`}>{t('edit')}</Tooltip>}
                      >
                        <Link
                          to={{
                            pathname: INVOICES_EDIT.replace(':id', invoice.Id),
                            search: `?returnTo=${encodeURIComponent(WITHHOLDINGS_DETAIL.replace(':id', id))}`,
                          }}
                          className="btn btn-link text-primary p-1"
                          aria-label={t('edit')}
                        >
                          <i className="fas fa-edit" aria-hidden="true" />
                        </Link>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`release-inv-${invoice.Id}`}>{t('release_from_withholding')}</Tooltip>}
                      >
                        <button
                          type="button"
                          className="btn btn-link text-warning p-1"
                          aria-label={t('release_from_withholding')}
                          disabled={actionsBusy}
                          onClick={() => handleReleaseInvoice(invoice)}
                        >
                          <i className="fas fa-unlink" aria-hidden="true" />
                        </button>
                      </OverlayTrigger>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="wh-detail-actions mt-4">
          <div className="wh-detail-actions__left">
            <DetailActionButton
              variant="outline-secondary"
              icon="fas fa-arrow-left"
              label={t('back')}
              disabled={actionsBusy}
              onClick={() => history.push(WITHHOLDINGS_LIST)}
            />
          </div>
          <div className="wh-detail-actions__right">
            <DetailActionButton
              variant="outline-primary"
              icon="fas fa-envelope"
              label={t('send_by_email')}
              disabled={actionsBusy}
              onClick={handleSendEmail}
            />
            <DetailActionButton
              variant="outline-primary"
              icon="fas fa-download"
              label={t('download')}
              disabled={actionsBusy}
              busy={downloading}
              onClick={handleDownload}
            />
            {canDelete && (
              <DetailActionButton
                variant="outline-danger"
                icon="fas fa-trash-alt"
                label={t('delete')}
                disabled={actionsBusy}
                busy={deleting}
                onClick={handleDelete}
              />
            )}
            {canEdit && (
              <DetailActionButton
                type="submit"
                variant="primary"
                icon="fas fa-save"
                label={saving ? t('saving') : t('save')}
                disabled={actionsBusy}
                busy={saving}
              />
            )}
          </div>
        </div>
      </Form>
      <WithholdingEmailSendModal />
    </div>
  )
}

export default WithholdingDetail
